'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check, ChevronDown } from 'lucide-react'
import posthog from 'posthog-js'
import Flag from './Flag'
import { locales, localeDir, localeHref, isLocale, type Locale } from '@/app/lib/i18n'

// Each language's name in that language, so a visitor who landed on the
// wrong one can still recognise their own.
const languageNames: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  'pt-BR': 'Português (Brasil)',
  'zh-CN': '简体中文',
  ja: '日本語',
  fr: 'Français',
  ar: 'العربية',
}

// `pathname` is the browser-visible URL (/es/faq, or /faq for English), not
// the internal /en-rewritten one - strip any locale prefix to get back the
// unprefixed path that localeHref expects.
function unprefixedPath(pathname: string): string {
  const firstSegment = pathname.split('/')[1]
  if (!isLocale(firstSegment)) return pathname
  return pathname.slice(firstSegment.length + 1) || '/'
}

/**
 * Flag dropdown linking to the current page in every shipped locale. Only
 * rendered on pages translated into all of them (landing, how-it-works,
 * faq, blog) - English-only pages (/download) and the app side (/package,
 * /payment-success, /admin) don't include it.
 *
 * Always sits on a white background: the landing page's top-right corner,
 * or just under the black PageHeader banner elsewhere.
 */
export default function LanguageSwitcher({
  locale,
  className,
}: {
  locale: Locale
  className?: string
}) {
  const path = unprefixedPath(usePathname() ?? '/')

  return (
    <div data-farf-site className={className}>
      <DropdownMenu.Root modal={false} dir={localeDir(locale)}>
        <DropdownMenu.Trigger
          aria-label={`Language: ${languageNames[locale]}`}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[13px] font-semibold leading-none outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#22c55e] border-black/10 bg-white text-[#111827] shadow-sm hover:bg-[#f3f4f6] data-[state=open]:bg-[#f3f4f6]"
        >
          <Flag locale={locale} />
          <span>{locale.split('-')[0].toUpperCase()}</span>
          <ChevronDown size={14} aria-hidden="true" className="opacity-70" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            data-farf-portal
            data-farf-locale-menu
            sideOffset={6}
            align="end"
            collisionPadding={8}
            className="z-[200] min-w-48 rounded-xl border border-black/10 bg-white p-1 text-start shadow-lg"
          >
            {locales.map((l) => (
              <DropdownMenu.Item key={l} asChild>
                <Link
                  href={localeHref(l, path)}
                  hrefLang={l}
                  lang={l}
                  onClick={() => {
                    if (l !== locale) posthog.capture('language_switched', { from: locale, to: l, path })
                  }}
                  className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-[#111827] no-underline outline-none transition-colors data-[highlighted]:bg-[#f3f4f6]"
                >
                  <Flag locale={l} />
                  <span className="flex-1">{languageNames[l]}</span>
                  {l === locale && <Check size={15} aria-hidden="true" className="text-[#22c55e]" />}
                </Link>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  )
}
